import { useMemo, useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import { getPartner, getPartnerSlots, getPartnerUsage, updatePartner, type WaPartner } from '@/api/partners'

function formatShekels(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`
}

function PartnerStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    active: { bg: '#14532d40', text: '#4ade80' },
    trial: { bg: '#78350f40', text: '#fbbf24' },
    suspended: { bg: '#7f1d1d40', text: '#f87171' },
    cancelled: { bg: '#37415160', text: '#9ca3af' },
  }
  const s = styles[status] ?? styles.cancelled
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium capitalize"
      style={{ background: s.bg, color: s.text }}
    >
      {status}
    </span>
  )
}

const STATUS_OPTIONS: WaPartner['status'][] = ['trial', 'active', 'suspended', 'cancelled']

export default function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: partner, isLoading: loadingPartner, error: partnerError } = useQuery({
    queryKey: ['wa-partner', id],
    queryFn: () => getPartner(id!),
    enabled: !!id,
  })

  const { data: slots = [], isLoading: loadingSlots } = useQuery({
    queryKey: ['partner-org-slots', id],
    queryFn: () => getPartnerSlots(id!),
    enabled: !!id,
  })

  const { data: usage = [], isLoading: loadingUsage } = useQuery({
    queryKey: ['wa-partner-usage', id],
    queryFn: () => getPartnerUsage(id!),
    enabled: !!id,
  })

  const [editRate, setEditRate] = useState('')
  const [editMaxSlots, setEditMaxSlots] = useState('')
  const [editStatus, setEditStatus] = useState<WaPartner['status']>('active')

  useEffect(() => {
    if (partner) {
      setEditRate(String(partner.daily_rate_agorot))
      setEditMaxSlots(String(partner.max_org_slots))
      setEditStatus(partner.status)
    }
  }, [partner])

  const monthlyRows = useMemo(() => {
    const map = new Map<string, { instanceDays: number; costAgorot: number }>()
    for (const row of usage) {
      const key = row.usage_date.slice(0, 7)
      const cur = map.get(key) ?? { instanceDays: 0, costAgorot: 0 }
      cur.instanceDays += 1
      cur.costAgorot += row.cost_agorot
      map.set(key, cur)
    }
    return Array.from(map.entries())
      .map(([month, v]) => ({ month, ...v }))
      .sort((a, b) => b.month.localeCompare(a.month))
  }, [usage])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!id || !partner) return
      const rate = Number.parseInt(editRate, 10)
      const maxSlots = Number.parseInt(editMaxSlots, 10)
      if (Number.isNaN(rate) || rate < 0) throw new Error(t('partners.invalidRate'))
      if (Number.isNaN(maxSlots) || maxSlots < 0) throw new Error(t('partners.invalidMaxSlots'))
      await updatePartner(id, {
        daily_rate_agorot: rate,
        max_org_slots: maxSlots,
        status: editStatus,
      })
    },
    onSuccess: () => {
      toast.success(t('partners.updated'))
      void queryClient.invalidateQueries({ queryKey: ['wa-partner', id] })
      void queryClient.invalidateQueries({ queryKey: ['wa-partners'] })
    },
    onError: (e: Error) => {
      toast.error(e.message || t('partners.failedToSave'))
    },
  })

  if (!id) {
    return (
      <div className="text-red-400 text-sm">{t('partners.missingId')}</div>
    )
  }

  if (loadingPartner) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    )
  }

  if (partnerError || !partner) {
    return (
      <div className="space-y-4">
        <Link
          to="/partners"
          className="inline-flex items-center gap-2 text-sm text-indigo-300 hover:text-indigo-200"
        >
          <ArrowLeft size={16} />
          {t('partners.backToPartners')}
        </Link>
        <p className="text-red-400">{t('partners.notFound')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/partners"
          className="inline-flex items-center gap-2 text-sm text-indigo-300 hover:text-indigo-200 mb-4"
        >
          <ArrowLeft size={16} />
          {t('partners.backToPartners')}
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-white">{partner.display_name}</h1>
          <PartnerStatusBadge status={partner.status} />
        </div>
      </div>

      <div
        className="rounded-xl p-4 sm:p-6 space-y-4"
        style={{ background: '#252836', border: '1px solid #2d3148' }}
      >
        <h2 className="text-white font-semibold text-sm" style={{ color: '#e5e7eb' }}>
          {t('partners.details')}
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <dt style={{ color: '#9ca3af' }}>{t('partners.slug')}</dt>
            <dd className="font-mono text-indigo-300 mt-0.5">{partner.slug}</dd>
          </div>
          <div>
            <dt style={{ color: '#9ca3af' }}>{t('partners.contactEmail')}</dt>
            <dd className="text-gray-200 mt-0.5">{partner.contact_email ?? '—'}</dd>
          </div>
          <div>
            <dt style={{ color: '#9ca3af' }}>{t('partners.contactPhone')}</dt>
            <dd className="text-gray-200 mt-0.5">{partner.contact_phone ?? '—'}</dd>
          </div>
          <div>
            <dt style={{ color: '#9ca3af' }}>{t('partners.billingModel')}</dt>
            <dd className="text-gray-200 mt-0.5">{partner.billing_model}</dd>
          </div>
          <div>
            <dt style={{ color: '#9ca3af' }}>{t('partners.dailyRate')}</dt>
            <dd className="text-gray-200 font-mono num mt-0.5">{formatShekels(partner.daily_rate_agorot)}</dd>
          </div>
          <div>
            <dt style={{ color: '#9ca3af' }}>{t('partners.maxSlots')}</dt>
            <dd className="text-gray-200 font-mono num mt-0.5">{partner.max_org_slots}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt style={{ color: '#9ca3af' }}>{t('partners.trialEnds')}</dt>
            <dd className="text-gray-200 mt-0.5">
              {partner.trial_ends_at ? new Date(partner.trial_ends_at).toLocaleString() : '—'}
            </dd>
          </div>
        </dl>

        <div
          className="pt-4 mt-2 border-t space-y-4"
          style={{ borderColor: '#2d3148' }}
        >
          <h3 className="text-sm font-medium text-white">{t('partners.editable')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs mb-1" style={{ color: '#9ca3af' }}>
                {t('partners.dailyRateLabel')}
              </label>
              <input
                type="number"
                min={0}
                value={editRate}
                onChange={(e) => setEditRate(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-white text-sm font-mono"
                style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
              />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: '#9ca3af' }}>
                {t('partners.maxSlots')}
              </label>
              <input
                type="number"
                min={0}
                value={editMaxSlots}
                onChange={(e) => setEditMaxSlots(e.target.value)}
                className="w-full rounded-lg px-3 py-2 text-white text-sm font-mono"
                style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
              />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: '#9ca3af' }}>
                {t('common.status')}
              </label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value as WaPartner['status'])}
                className="w-full rounded-lg px-3 py-2 text-white text-sm"
                style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Button
            size="sm"
            loading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-white">{t('partners.instancesSection')}</h2>
        <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
          <table className="min-w-full text-sm" style={{ background: '#1a1d27' }}>
            <thead>
              <tr style={{ background: '#252836', color: '#9ca3af' }}>
                <th className="text-left font-medium px-4 py-3">{t('partners.orgId')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.phone')}</th>
                <th className="text-left font-medium px-4 py-3">{t('common.status')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.lastConnected')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.dailyMessages')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {loadingSlots && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loadingSlots && slots.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>
                    {t('partners.noInstances')}
                  </td>
                </tr>
              )}
              {!loadingSlots &&
                slots.map((row) => (
                  <tr key={`${row.org_id}-${row.partner_id ?? ''}`} style={{ borderTop: '1px solid #2d3148' }}>
                    <td className="px-4 py-3 font-mono text-xs text-indigo-300">{row.org_id}</td>
                    <td className="px-4 py-3 font-mono text-gray-300">{row.phone_number ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-200">{row.status ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {row.last_connected_at ? new Date(row.last_connected_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-300 num">
                      {row.daily_message_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate" title={row.notes ?? ''}>
                      {row.notes ?? '—'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-white">{t('partners.monthlyUsage')}</h2>
        <p className="text-xs" style={{ color: '#9ca3af' }}>
          {t('partners.usageHint')}
        </p>
        <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
          <table className="min-w-full text-sm" style={{ background: '#1a1d27' }}>
            <thead>
              <tr style={{ background: '#252836', color: '#9ca3af' }}>
                <th className="text-left font-medium px-4 py-3">{t('partners.month')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.totalInstanceDays')}</th>
                <th className="text-left font-medium px-4 py-3">{t('partners.totalCost')}</th>
              </tr>
            </thead>
            <tbody>
              {loadingUsage && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loadingUsage && monthlyRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>
                    {t('partners.noUsage')}
                  </td>
                </tr>
              )}
              {!loadingUsage &&
                monthlyRows.map((r) => (
                <tr key={r.month} style={{ borderTop: '1px solid #2d3148' }}>
                  <td className="px-4 py-3 text-white font-mono">{r.month}</td>
                  <td className="px-4 py-3 font-mono text-gray-300 num">{r.instanceDays}</td>
                  <td className="px-4 py-3 font-mono text-gray-200 num">{formatShekels(r.costAgorot)}</td>
                </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
